// Shared primitives for the clinical dashboard panels.
import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";

export function Section({ title, sub, right, children, testid, tone }) {
  const { t } = useTranslation();
  return (
    <section data-testid={testid}
      className={`rounded-lg border bg-white ${tone || "border-[#E5DFD3]"}`}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[#EFE9DE] px-4 py-3">
        <div>
          <h2 className="font-serif text-lg font-bold text-stone-900">{t(title)}</h2>
          {sub && <p className="text-xs text-stone-500">{t(sub)}</p>}
        </div>
        {right}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Empty({ children }) {
  const { t } = useTranslation();
  return <p className="text-sm italic text-stone-400">{t(children)}</p>;
}

export function Label({ children }) {
  const { t } = useTranslation();
  return <span className="font-mono text-[11px] uppercase tracking-wider text-stone-400">{t(children)}</span>;
}

/** A score 0–10 rendered in its interpretation colour. */
export function Score({ value, color, size = "base" }) {
  const cls = { sm: "text-sm", base: "text-xl", lg: "text-3xl" }[size] || "text-xl";
  return (
    <span className={`font-serif font-bold tabular-nums ${cls}`} style={{ color: color || "#78716c" }}>
      {value ?? "—"}
    </span>
  );
}

/** Signed change with an arrow. Improvement is green because higher = better. */
export function Delta({ value, testid }) {
  if (value == null) return <span className="text-stone-300">—</span>;
  const flat = Math.abs(value) < 0.05;
  const Icon = flat ? Minus : value > 0 ? ArrowUp : ArrowDown;
  const color = flat ? "#78716c" : value > 0 ? "#15803D" : "#DC2626";
  return (
    <span data-testid={testid} className="inline-flex items-center gap-0.5 font-mono text-sm tabular-nums"
      style={{ color }}>
      <Icon className="h-3.5 w-3.5" />
      {flat ? "0" : `${value > 0 ? "+" : ""}${value}`}
    </span>
  );
}

/** Horizontal 0–10 meter. */
export function Meter({ value, color }) {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, (value / 10) * 100));
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
      <div className="h-full rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: color || "#A8A29E" }} />
    </div>
  );
}

/** Key/value pair for the identity strip. */
export function Fact({ label, value, wide }) {
  const { t } = useTranslation();
  if (!value) return null;
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label>{label}</Label>
      <div className="text-sm text-stone-800">{t(value)}</div>
    </div>
  );
}

export function Chip({ children, tone = "stone", title }) {
  const tones = {
    stone: "border-[#E5DFD3] bg-stone-50 text-stone-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
  };
  return (
    <span title={title}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs ${tones[tone] || tones.stone}`}>
      {children}
    </span>
  );
}
